/**
 * ReviewTable — maps BulkClassified → review rows; manages the live preview
 * (dryRun) and the save flow (dryRun=false in chunks of 100).
 *
 * Step 7 wiring:
 * - When rows change, fires a debounced (400 ms) bulk dryRun to preview
 *   figuresAfter / refused / warnings / firstRun.
 * - Self-serve refused → « Ajouter N » greyed, LimitReasonLine above foot.
 * - Agency non-empty warnings → QuotaWarningDialog on « Ajouter N » click.
 * - Save: chunks of 100; per-row errors inline; 409 ALLOWANCE_EXCEEDED stops
 *   sequence; calls onCreated(created, firstRun) after all chunks.
 *
 * @see B-popup-frontend.md step 5/7, UI-SPEC §2.16
 */
import { Box, Button, Tooltip } from '@mui/material';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { BrandQuery, BrandQueryAxis, FirstRunNotice, SaveWarnings, AllowanceFigures, BrandQuerySource, TargetZone } from '@traaker/shared';
import { promptPlaceKey, zoneDisplayLabel } from '@traaker/shared';
import type { ReviewRow } from './studioReducer';
import type { LimitSlot, LocKey, PromptAllowanceFigures } from './promptStudioModel';
import {
  AXIS_COLOR,
  MONDIAL,
  PROMPT_ZONE_EDITING,
  placeOptionsOf,
  firstRunLines,
  structuralRowError,
  offeredAxes,
  AXIS_SPLIT_LIVE,
  limitReason,
  leftLines,
  slotText,
} from './promptStudioModel';
import { brandQueriesApi } from '../../services/brand-queries';
import { chunk } from './composerModel';
import { LimitReasonLine } from './LimitReasonLine';
import { QuotaWarningDialog } from './QuotaWarningDialog';
import { LevelLine } from './ClassificationLines';
import { ImageReviewBlock, TopicReviewBlock } from './ImageReviewBlock';
import type { PickedTarget } from './TargetChip';
import { targetsService } from '../../services/targets';
import { LocChip } from './PromptRow';
import { Pick } from './Pick';
import { Flag } from '../../pages/dashboard/Flag';
import { languageFlag } from '../../pages/dashboard/geoFlags';
import type { ComplementKind } from '@traaker/shared';
import type { ReactNode } from 'react';

export interface ReviewTableProps {
  rows: ReviewRow[];
  source: string;
  bulkSource: BrandQuerySource;
  bulkDefaults?: { tags?: string[]; zoneMeta?: unknown };
  /** The row place picker's listed zones (promptPickerZones). */
  placeZones?: Map<LocKey, TargetZone>;
  /** Place search language (brand working language). */
  lang?: string;
  mode: 'batch' | 'edit';
  brandId: string;
  isAgency: boolean;
  currentFigures?: PromptAllowanceFigures | null;
  monthFigure: ReactNode | null;
  rateLimited: number;
  /** Used only in edit mode or as initial disabled state; live state managed by preview */
  submitDisabled?: boolean;
  firstRun: FirstRunNotice[] | null;
  onChange: (rows: ReviewRow[]) => void;
  onRetry: (row: ReviewRow) => void;
  onBack?: () => void;
  onCreated: (rows: BrandQuery[], firstRun?: FirstRunNotice[]) => void;
  /** Lot F step 14: the brand's criteria list. */
  criteriaOptions?: string[];
  /** The brand's themes (authority, `keywords`) and use cases (referral, `scenarios`). */
  topicOptions?: { authority: string[]; referral: string[] };
}

// ---------------------------------------------------------------------------
// internal preview state
// ---------------------------------------------------------------------------

type PreviewStatus = 'idle' | 'loading' | 'done' | 'failed';

interface PreviewState {
  status: PreviewStatus;
  seq: number;
  figuresAfter?: AllowanceFigures;
  refused?: boolean;
  warnings?: SaveWarnings[];
  firstRun?: FirstRunNotice[];
}

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------

export function ReviewTable({
  rows,
  source,
  bulkSource,
  bulkDefaults,
  placeZones,
  lang,
  mode,
  brandId,
  isAgency,
  currentFigures,
  monthFigure,
  rateLimited,
  submitDisabled,
  firstRun,
  onChange,
  onRetry,
  onBack,
  onCreated,
  criteriaOptions = [],
  topicOptions = { authority: [], referral: [] },
}: ReviewTableProps) {
  const offered = offeredAxes(AXIS_SPLIT_LIVE);

  // Target names for the rows' targets (detected or picked): one by-id lookup for the ids not known yet.
  const [targetNames, setTargetNames] = useState<Record<string, { label: string; category: string }>>({});
  useEffect(() => {
    const missing = [...new Set(rows.flatMap((r) => (r.targetId && !targetNames[r.targetId] ? [r.targetId] : [])))];
    if (missing.length === 0) return;
    targetsService.getByIds(brandId, missing.map((targetId) => ({ targetId }))).then(
      (res) =>
        setTargetNames((m) => {
          const next = { ...m };
          for (const it of res.items) next[it.targetId] = { label: it.label ?? it.url ?? it.targetId, category: it.category };
          // i18n: promptStudio.targetChip.unresolved
          for (const id of res.notFound) next[id] = { label: 'Cible non résolue', category: '' };
          return next;
        }),
      () => {},
    );
  }, [rows, brandId, targetNames]);
  const rowTarget = (r: ReviewRow): PickedTarget | null =>
    r.targetId
      ? {
          targetId: r.targetId,
          category: r.targetCategory ?? targetNames[r.targetId]?.category ?? '',
          label: targetNames[r.targetId]?.label ?? '…',
        }
      : null;

  const hasImageOrComparison = rows.some(
    (r) => r.axis === 'narrative' || r.axis === 'comparison',
  );

  const { t } = useTranslation();

  // ---- preview state -------------------------------------------------------
  const previewSeqRef = useRef(0);
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle', seq: 0 });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- save state ----------------------------------------------------------
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rowInlineErrors, setRowInlineErrors] = useState<Record<number, string>>({});
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);

  // ---- row place (UI-SPEC §2.16): the batch place unless the row has its own (null = Mondial)
  const [pickRow, setPickRow] = useState<number | null>(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const batchZone = (bulkDefaults?.zoneMeta ?? null) as TargetZone | null;
  const rowZone = (r: ReviewRow) =>
    r.zoneMeta !== undefined ? (r.zoneMeta as TargetZone | null) : batchZone;
  const pickedRow = pickRow !== null ? rows[pickRow] : undefined;
  const pickedZone = pickedRow ? rowZone(pickedRow) : null;
  const pickZones = placeOptionsOf([batchZone, pickedZone], placeZones ?? new Map());

  // ---- fire debounced preview on rows change --------------------------------
  const firePreview = useCallback(async (currentRows: ReviewRow[], seq: number) => {
    if (currentRows.length === 0) {
      setPreview({ status: 'idle', seq });
      return;
    }
    setPreview((p) => ({ ...p, status: 'loading', seq }));
    try {
      const items = rowsToBulkItems(currentRows);
      const res = await brandQueriesApi.bulk(brandId, {
        items,
        dryRun: true,
        source: bulkSource,
        ...(bulkDefaults ? { defaults: bulkDefaults as any } : {}),
      });
      // Drop stale results
      if (seq !== previewSeqRef.current) return;
      const bulk = res as any;
      setPreview({
        status: 'done',
        seq,
        figuresAfter: bulk.figuresAfter,
        refused: bulk.refused,
        warnings: bulk.warnings,
        firstRun: bulk.firstRun,
      });
    } catch {
      if (seq !== previewSeqRef.current) return;
      setPreview((p) => ({ ...p, status: 'failed', seq }));
    }
  }, [brandId, bulkSource, bulkDefaults]);

  useEffect(() => {
    const seq = ++previewSeqRef.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      firePreview(rows, seq);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rows, firePreview]);

  // Immediately fire on mount (no debounce for initial open)
  useEffect(() => {
    const seq = ++previewSeqRef.current;
    firePreview(rows, seq);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- derived limit state -------------------------------------------------
  // Self-serve: refused → show limit reason
  const limitState: { reason: LimitSlot | null; left: LimitSlot[] } | null =
    !isAgency && preview.status === 'done' && preview.refused && preview.figuresAfter
      ? {
          reason: limitReason(preview.figuresAfter, 'promptRequests'),
          left: leftLines(currentFigures ?? null, rows.map((r) => r.axis).filter(Boolean) as BrandQueryAxis[]),
        }
      : null;

  const previewFailed = preview.status === 'failed';
  const previewLoading = preview.status === 'loading' || preview.status === 'idle';

  // « Ajouter N » disabled when: saving, preview in flight/failed, or self-serve refused
  const submitOk = !saving && !previewLoading && !previewFailed && !limitState && !submitDisabled;

  // ---- save ----------------------------------------------------------------
  const runSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setRowInlineErrors({});

    const allRows = [...rows];
    const chunks = chunk(allRows, 100);
    const allCreated: BrandQuery[] = [];
    let mergedFirstRun: FirstRunNotice[] | undefined;
    let inlineErrors: Record<number, string> = {};
    let offset = 0;

    for (const ch of chunks) {
      // Build items without rows that already errored
      const chunkItems = ch.map((r) => rowToBulkItem(r));
      try {
        const res = await brandQueriesApi.bulk(brandId, {
          items: chunkItems,
          dryRun: false,
          source: bulkSource,
          ...(bulkDefaults ? { defaults: bulkDefaults as any } : {}),
        });
        const bulk = res as any;

        // Collect created rows
        if (Array.isArray(bulk.created)) {
          allCreated.push(...bulk.created);
        }
        // Collect per-row errors
        if (Array.isArray(bulk.errors)) {
          for (const e of bulk.errors) {
            if (typeof e.index === 'number') {
              inlineErrors[offset + e.index] = e.error ?? 'Erreur';
            }
          }
        }
        // Merge firstRun
        if (bulk.firstRun) {
          mergedFirstRun = mergeFirstRunArrays(mergedFirstRun, bulk.firstRun);
        }
      } catch (err: unknown) {
        const e = err as { status?: number; body?: { code?: string; figuresAfter?: AllowanceFigures } };
        if (e?.status === 409 && e.body?.code === 'ALLOWANCE_EXCEEDED') {
          // Stop sequence; update preview limit state from 409 body
          if (e.body.figuresAfter) {
            setPreview({
              status: 'done',
              seq: previewSeqRef.current,
              figuresAfter: e.body.figuresAfter,
              refused: true,
            });
          }
          setSaveError(null);
          break;
        }
        setSaveError('promptStudio.common.saveFailed');
        // i18n: Enregistrement impossible, réessayez.
        break;
      }
      offset += ch.length;
    }

    // Update rows: remove ones that saved cleanly, keep errored ones
    if (Object.keys(inlineErrors).length > 0) {
      setRowInlineErrors(inlineErrors);
      // Keep only rows with errors
      const remaining = rows.filter((_, i) => inlineErrors[i]);
      onChange(remaining);
    } else {
      onChange([]);
    }

    setSaving(false);

    if (allCreated.length > 0) {
      onCreated(allCreated, mergedFirstRun);
    }
  }, [rows, brandId, bulkSource, bulkDefaults, onChange, onCreated]);

  const handleSubmitClick = () => {
    if (!submitOk) return;
    // Agency: open QuotaWarningDialog if preview has warnings
    if (isAgency && preview.warnings && preview.warnings.length > 0) {
      setQuotaDialogOpen(true);
      return;
    }
    runSave();
  };

  // ---- first-run projected lines -------------------------------------------
  const previewFirstRunEntries = !previewLoading && !previewFailed
    ? firstRunLines(preview.firstRun ?? null)
    : [];
  const saveFirstRunEntries = firstRunLines(firstRun);

  // ---- row helpers ---------------------------------------------------------
  const updateRow = (i: number, patch: Partial<ReviewRow>) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };

  const removeRow = (i: number) => {
    onChange(rows.filter((_, idx) => idx !== i));
  };

  const addN = rows.filter((r) => !r.error).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Source line */}
      <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {/* i18n: promptStudio.review.source */}
        {source} · {rows.length} question{rows.length > 1 ? 's' : ''}
        {hasImageOrComparison && (
          <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
            {/* i18n: promptStudio.review.imageCriteria */}
            · Précisez l'axe si besoin
          </Box>
        )}
      </Box>

      {/* Rate-limited warning */}
      {rateLimited > 0 && (
        <Box sx={{ px: 2, py: 0.75, bgcolor: '#fffbeb', fontSize: 12, color: '#b45309', flexShrink: 0 }}>
          {/* i18n: promptStudio.review.rateLimited */}
          Limite de classement atteinte : {rateLimited} question{rateLimited > 1 ? 's restent' : ' reste'} en attente. Réessayez dans une heure.
        </Box>
      )}

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {rows.map((row, i) => {
          const isPending = !row.intent;
          const rowError = rowInlineErrors[i] ?? row.error;
          const structErr = rowError
            ? structuralRowError(rowError, row.axis ?? 'narrative', 'save')
            : null;
          const displayError = structErr ? structErr.key : rowError;

          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 2,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'rgba(0,0,0,.05)',
                '&:hover': { bgcolor: 'rgba(0,0,0,.01)' },
              }}
            >
              {/* Axis picker dots */}
              <Box sx={{ display: 'flex', gap: 0.5, pt: 0.25, flexShrink: 0 }}>
                {offered.map((a) => (
                  <Box
                    key={a}
                    component="button"
                    title={a}
                    onClick={() => {
                      const intent = a === 'authority' ? 'learn' :
                        a === 'referral' ? 'recommend' :
                        a === 'comparison' ? 'compare' :
                        (row.intent === 'describe' || row.intent === 'reputation') ? row.intent : 'describe';
                      // A topic is a term of the classified axis's vocabulary.
                      updateRow(i, { axis: a, intent, ...(a !== row.axis ? { topic: undefined } : {}) });
                    }}
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      border: 'none',
                      bgcolor: row.axis === a ? AXIS_COLOR[a] : 'transparent',
                      outline: `1.5px solid ${AXIS_COLOR[a]}`,
                      cursor: 'pointer',
                      p: 0,
                      flexShrink: 0,
                    }}
                  />
                ))}
                {isPending && (
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      outline: '1.5px solid #f59e0b',
                      bgcolor: 'transparent',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>

              {/* Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 13, wordBreak: 'break-word' }}>
                  {row.text}
                  {row.text.length > 300 && (
                    <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: '#b45309' }}>
                      {/* i18n: promptStudio.review.overCap */}
                      {row.text.length} / 300 · Au delà de 300 caractères : cette question ne sera pas enregistrée.
                    </Box>
                  )}
                </Box>

                {/* Classification facts (UI-SPEC §2.16): level on referral, criteria / theme / use case */}
                {!isPending && (
                  <Box sx={{ mt: 0.5 }}>
                    {row.axis === 'referral' && <LevelLine complements={row.complements as ComplementKind[] | undefined} zone={rowZone(row)} />}
                    {row.axis === 'narrative' || row.axis === 'comparison' ? (
                      <ImageReviewBlock
                        brandId={brandId}
                        criteriaOptions={criteriaOptions}
                        criteria={row.criteria}
                        target={rowTarget(row)}
                        targetLookup={row.targetLookup}
                        disabled={saving}
                        onCriteriaChange={(criteria) => updateRow(i, { criteria })}
                        onTargetChange={(t) => {
                          if (t) setTargetNames((m) => ({ ...m, [t.targetId]: { label: t.label, category: t.category } }));
                          updateRow(i, { targetId: t?.targetId, targetCategory: t?.category, targetLookup: undefined });
                        }}
                        onRetryDetection={() => onRetry(row)}
                      />
                    ) : row.axis === 'authority' || row.axis === 'referral' ? (
                      <TopicReviewBlock
                        axis={row.axis}
                        topicOptions={topicOptions[row.axis]}
                        topic={row.topic}
                        disabled={saving}
                        onTopicChange={(topic) => updateRow(i, { topic })}
                      />
                    ) : null}
                  </Box>
                )}

                {/* Pending row retry */}
                {isPending && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Box
                      component="button"
                      onClick={() => onRetry(row)}
                      sx={{
                        fontSize: 11.5,
                        color: '#7c3aed',
                        border: 'none',
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        p: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      {/* i18n: promptStudio.review.retry */}
                      réessayer
                    </Box>
                  </Box>
                )}

                {/* Row error */}
                {displayError && (
                  <Box sx={{ fontSize: 11.5, color: '#b45309', mt: 0.25 }}>
                    {displayError}
                  </Box>
                )}
              </Box>

              {/* Place (opens the place picker; this row only) */}
              <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                <LocChip
                  zone={rowZone(row)}
                  onClick={PROMPT_ZONE_EDITING ? () => { setPlaceQuery(''); setPickRow(i); } : undefined}
                />
              </Box>

              {/* Language (flag, the detected language) */}
              <Box sx={{ width: 28, flexShrink: 0, pt: 0.25, display: 'flex', justifyContent: 'center' }}>
                {row.language && (
                  languageFlag(row.language)
                    ? <Flag countryCode={languageFlag(row.language)} size={18} title={row.language} />
                    : <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>{row.language.toUpperCase()}</Box>
                )}
              </Box>

              {/* Remove */}
              <Box
                component="button"
                onClick={() => removeRow(i)}
                sx={{ fontSize: 16, color: 'text.disabled', border: 'none', bgcolor: 'transparent', cursor: 'pointer', flexShrink: 0, pt: 0.25 }}
              >
                ✕
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {/* Preview failed error */}
        {previewFailed && (
          <Box sx={{ mb: 1, fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* i18n: promptStudio.fit.previewFailed */}
            Impossible de vérifier ce que cette modification consomme, réessayez.
            <Box
              component="button"
              onClick={() => {
                const seq = ++previewSeqRef.current;
                firePreview(rows, seq);
              }}
              sx={{ fontSize: 12, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, textDecoration: 'underline' }}
            >
              {/* i18n: promptStudio.load.retry */}
              Réessayer
            </Box>
          </Box>
        )}

        {/* Save error */}
        {saveError && (
          <Box sx={{ mb: 1, fontSize: 12, color: '#b45309' }}>
            {/* i18n: promptStudio.common.saveFailed */}
            Enregistrement impossible, réessayez.
          </Box>
        )}

        {/* Limit reason (self-serve refused) */}
        {limitState && (
          <LimitReasonLine reason={limitState.reason} left={limitState.left} />
        )}

        {/* Month figure */}
        {monthFigure && (
          <Box sx={{ mb: 1, textAlign: 'right', fontSize: 12, color: 'text.secondary' }}>
            {monthFigure}
          </Box>
        )}

        {/* First-run projected lines (from preview, while preview is done and not stale) */}
        {previewFirstRunEntries.map((l) => {
          const axisLabel = axisWord(l.axis);
          const text = firstRunText(l, axisLabel);
          return (
            <Box key={`preview-${l.axis}-${l.key}`} sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
              {text}
            </Box>
          );
        })}

        {/* First-run lines from the save responses (used in edit mode) */}
        {saveFirstRunEntries.map((l) => {
          const axisLabel = axisWord(l.axis);
          const text = firstRunText(l, axisLabel);
          return (
            <Box key={`save-${l.axis}-${l.key}`} sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
              {text}
            </Box>
          );
        })}

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
          {mode === 'batch' && onBack && (
            <Button variant="text" onClick={onBack} size="small">
              {/* i18n: common.back */}
              Retour
            </Button>
          )}
          {mode === 'edit' && (
            <Button variant="text" onClick={onBack} size="small">
              {/* i18n: common.cancel */}
              Annuler
            </Button>
          )}
          <Tooltip
            title={limitState?.reason ? slotText(t, limitState.reason) : previewFailed ? t('promptStudio.fit.previewFailed') : ''}
            arrow
          >
            <Box component="span">
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmitClick}
                disabled={!submitOk}
                sx={{ fontWeight: 600 }}
              >
                {mode === 'batch' ? (
                  /* i18n: promptStudio.review.add */
                  saving ? 'Enregistrement…' : `Ajouter ${addN}`
                ) : (
                  /* i18n: common.save */
                  saving ? 'Enregistrement…' : 'Enregistrer'
                )}
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {pickedRow && pickRow !== null && (
        <Pick
          open
          kind="place"
          selected={pickedZone ? promptPlaceKey(pickedZone) : MONDIAL}
          onToggle={(key) => {
            updateRow(pickRow, { zoneMeta: key === MONDIAL ? null : pickZones.get(key) ?? null });
            setPickRow(null);
          }}
          onPickZone={(zone) => {
            updateRow(pickRow, { zoneMeta: zone });
            setPickRow(null);
          }}
          onClose={() => setPickRow(null)}
          placeOptions={[...pickZones].map(([key, zone]) => ({ key, zone, label: zoneDisplayLabel(zone) }))}
          searchQuery={placeQuery}
          onSearchChange={setPlaceQuery}
          lang={lang}
        />
      )}

      {/* Agency: quota warning dialog */}
      <QuotaWarningDialog
        open={quotaDialogOpen}
        warnings={preview.warnings ?? null}
        onConfirm={() => {
          setQuotaDialogOpen(false);
          runSave();
        }}
        onCancel={() => setQuotaDialogOpen(false)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function rowToBulkItem(r: ReviewRow) {
  return {
    text: r.text,
    intent: r.intent as any,
    language: r.language,
    complements: r.complements as any,
    topic: r.topic,
    criteria: r.criteria,
    targetId: r.targetId,
    zoneMeta: r.zoneMeta as any,
  };
}

function rowsToBulkItems(rows: ReviewRow[]) {
  return rows.map(rowToBulkItem);
}

function mergeFirstRunArrays(
  a: FirstRunNotice[] | undefined,
  b: FirstRunNotice[] | undefined,
): FirstRunNotice[] | undefined {
  if (!a) return b;
  if (!b) return a;
  const map = new Map<string, FirstRunNotice>();
  for (const e of [...a, ...b]) {
    const key = `${e.axis}|${e.when}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...e });
    } else {
      // Merge: add counts, keep earliest nextLaunchAt
      const merged: FirstRunNotice = {
        ...existing,
        count: (existing.count ?? 0) + (e.count ?? 0),
      };
      if (e.nextLaunchAt && (!existing.nextLaunchAt || e.nextLaunchAt < existing.nextLaunchAt)) {
        merged.nextLaunchAt = e.nextLaunchAt;
      }
      map.set(key, merged);
    }
  }
  return Array.from(map.values());
}

function axisWord(axis: BrandQueryAxis): string {
  const map: Record<BrandQueryAxis, string> = {
    narrative: 'Image',
    authority: 'Autorité',
    referral: 'Recommandation',
    comparison: 'Comparaison',
  };
  return map[axis] ?? axis;
}

function firstRunText(l: { key: string; count: number; date?: string }, axisLabel: string): string {
  if (l.key === 'promptStudio.firstRun.now') {
    return `${l.count} prompt${l.count > 1 ? 's' : ''} ${axisLabel} lancés maintenant.`;
  }
  if (l.date) {
    return `${l.count} prompt${l.count > 1 ? 's' : ''} ${axisLabel} au prochain lancement (${l.date}).`;
  }
  return `${l.count} prompt${l.count > 1 ? 's' : ''} ${axisLabel} au prochain lancement programmé.`;
}
